/**
 * Is this build the one that is currently released?
 *
 * A running client cannot answer that alone — it knows only the version frozen
 * into it — so this asks /api/v1/meta what the current release is and compares.
 *
 * Deliberately passive. There is no banner, no modal, and no notification: the
 * answer surfaces only on the About and Contact screens. That is a product
 * decision, not an oversight. On web it barely matters — index.html is served
 * no-cache while /assets/ is immutable, so any reload lands the new bundle and
 * staleness resolves itself. On native the App Store and Play Store already own
 * the update prompt, and duplicating it in-app would be noise.
 *
 * FAILS SILENT. Any error, any missing field, any unparseable version yields
 * 'unknown', and callers render the version with no currency claim — the same
 * shape as fetchLegalManifest returning null rather than nagging on a bad
 * network. Being wrong about this is worse than being quiet: telling someone to
 * update to something that does not exist is the failure mode the whole design
 * is arranged to avoid.
 */

import { useQuery } from '@tanstack/react-query';

import { metaApi, type Meta } from '../api/client';
import { compareVersions, currentVersion, platformKey, type PlatformKey } from '../utils/appVersion';

export type VersionStatus = 'current' | 'outdated' | 'unknown';

function releasedFor(meta: Meta, platform: PlatformKey): string {
  if (platform === 'ios') return meta.ios_release;
  if (platform === 'android') return meta.android_release;
  return meta.web_release;
}

export function useVersionStatus(): { version: string; status: VersionStatus } {
  const version = currentVersion();

  const { data } = useQuery({
    queryKey: ['meta'],
    queryFn: async () => (await metaApi.get()).data,
    // The answer changes only on deploy, and nothing here is urgent enough to
    // justify chasing it. One retry-free attempt per screen visit is plenty.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  if (!data) return { version, status: 'unknown' };

  const platform = platformKey();
  const order = compareVersions(version, releasedFor(data, platform));
  if (order === null) return { version, status: 'unknown' };

  // Only strictly-older counts as outdated. On native, a client AHEAD of the
  // store release is a TestFlight or internal build, which must not be told it
  // is behind the thing it is ahead of.
  //
  // On web, ahead is impossible rather than unusual: this backend served the
  // bundle, so the client cannot outrank it. Treat it as bad data — or as the
  // brief window during a rolling deploy where a fresh tab queries an old pod.
  // Kept identical to version_status() in backend/app/utils/version.py; if the
  // two drift, a support ticket and the user's own Settings screen will
  // disagree about the same build.
  if (order > 0) return { version, status: platform === 'web' ? 'unknown' : 'current' };
  return { version, status: order < 0 ? 'outdated' : 'current' };
}
